import { useState } from "react";

const SYSTEM_STATS = [
  { label: "Server Uptime", value: "99.97%", icon: "🟢", accent: "#6EE7B7", sub: "30-day average" },
  { label: "API Calls Today", value: "842K", icon: "⚡", accent: "#60A5FA", sub: "+12% vs yesterday" },
  { label: "Active Sessions", value: "3,241", icon: "👥", accent: "#A78BFA", sub: "Right now" },
  { label: "Error Rate", value: "0.03%", icon: "🔴", accent: "#F87171", sub: "Last 24 hours" },
];

const AUDIT_LOG = [
  { user: "James Doe", action: "Updated billing plan", time: "2 min ago", type: "billing", ip: "192.168.1.1" },
  { user: "Sarah Johnson", action: "Added new user: Marcus Chen", time: "14 min ago", type: "user", ip: "192.168.1.4" },
  { user: "System", action: "Automatic backup completed", time: "1h ago", type: "system", ip: "—" },
  { user: "David Kim", action: "Revoked API key: dev-token-3", time: "2h ago", type: "security", ip: "10.0.0.5" },
  { user: "James Doe", action: "Exported user data CSV", time: "3h ago", type: "data", ip: "192.168.1.1" },
  { user: "System", action: "SSL certificate renewed", time: "6h ago", type: "system", ip: "—" },
  { user: "Priya Patel", action: "Changed account status: inactive", time: "8h ago", type: "user", ip: "10.0.0.8" },
];

const PERMISSIONS = [
  { role: "Admin", permissions: ["Dashboard", "Analytics", "Users", "Settings", "Admin Panel", "Billing", "API Keys"], color: "#F87171" },
  { role: "Manager", permissions: ["Dashboard", "Analytics", "Users", "Settings"], color: "#FBBF24" },
  { role: "Analyst", permissions: ["Dashboard", "Analytics"], color: "#60A5FA" },
  { role: "Developer", permissions: ["Dashboard", "Analytics", "API Keys"], color: "#A78BFA" },
];

const TYPE_STYLE = {
  billing: { color: "#FBBF24", bg: "rgba(251,191,36,0.1)", label: "💳 billing" },
  user: { color: "#60A5FA", bg: "rgba(96,165,250,0.1)", label: "👤 user" },
  system: { color: "#8892a4", bg: "rgba(136,146,164,0.08)", label: "⚙️ system" },
  security: { color: "#F87171", bg: "rgba(248,113,113,0.1)", label: "🔒 security" },
  data: { color: "#6EE7B7", bg: "rgba(110,231,183,0.1)", label: "📊 data" },
};

export default function AdminPanel({ user }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [maintenance, setMaintenance] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [backupRunning, setBackupRunning] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const runBackup = () => {
    setBackupRunning(true);
    setTimeout(() => { setBackupRunning(false); showToast("Backup completed successfully!"); }, 3000);
  };

  if (user?.role !== "Admin") {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400, textAlign: "center" }}>
        <div style={{ fontSize: 64, marginBottom: 20 }}>🔒</div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: "#E8EDF5", marginBottom: 10 }}>Access Restricted</h2>
        <p style={{ fontSize: 14, color: "#8892a4", maxWidth: 360 }}>You need Admin privileges to access the Admin Panel. Contact your administrator.</p>
        <p style={{ fontSize: 12, color: "#4B5563", marginTop: 16 }}>Sign in as: admin@insightforge.io / admin123</p>
      </div>
    );
  }

  const tabs = ["overview", "permissions", "audit log", "system"];

  return (
    <div>
      {toast && (
        <div style={{ position: "fixed", top: 80, right: 24, zIndex: 1000, background: toast.type === "error" ? "rgba(248,113,113,0.15)" : "rgba(110,231,183,0.15)", border: `1px solid ${toast.type === "error" ? "rgba(248,113,113,0.3)" : "rgba(110,231,183,0.3)"}`, borderRadius: 12, padding: "12px 20px", color: toast.type === "error" ? "#F87171" : "#6EE7B7", fontSize: 14, fontWeight: 600, boxShadow: "0 8px 30px rgba(0,0,0,0.4)", backdropFilter: "blur(10px)", animation: "fadeIn 0.3s ease" }}>
          ✅ {toast.msg}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: "#E8EDF5" }}>Admin Panel</h1>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#F87171", background: "rgba(248,113,113,0.12)", border: "1px solid rgba(248,113,113,0.2)", padding: "2px 8px", borderRadius: 6, letterSpacing: "0.06em" }}>RESTRICTED</span>
          </div>
          <p style={{ fontSize: 13, color: "#8892a4" }}>System management and administration</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={runBackup} disabled={backupRunning} style={{ padding: "9px 16px", background: backupRunning ? "rgba(96,165,250,0.06)" : "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.25)", borderRadius: 10, color: "#60A5FA", fontSize: 13, fontWeight: 600, cursor: backupRunning ? "not-allowed" : "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
            {backupRunning ? "⏳ Backing up..." : "💾 Run Backup"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: 4, width: "fit-content" }}>
        {tabs.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            style={{ padding: "8px 18px", borderRadius: 9, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: "inherit", textTransform: "capitalize", transition: "all 0.2s", background: activeTab === tab ? "rgba(248,113,113,0.12)" : "transparent", color: activeTab === tab ? "#F87171" : "#8892a4" }}>
            {tab}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <div>
          {/* System stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 20 }}>
            {SYSTEM_STATS.map((s, i) => (
              <div key={i} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14, padding: "18px 20px", transition: "all 0.2s", cursor: "default" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = `${s.accent}30`; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"; }}>
                <div style={{ fontSize: 22, marginBottom: 10 }}>{s.icon}</div>
                <p style={{ fontSize: 11, fontWeight: 700, color: "#4B5563", letterSpacing: "0.07em", marginBottom: 6 }}>{s.label.toUpperCase()}</p>
                <p style={{ fontSize: 24, fontWeight: 800, color: "#E8EDF5", marginBottom: 4 }}>{s.value}</p>
                <p style={{ fontSize: 11, color: "#8892a4" }}>{s.sub}</p>
              </div>
            ))}
          </div>

          {/* Controls */}
          <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 16, padding: "24px", marginBottom: 20 }}>
            <h3 style={{ fontSize: 15, fontWeight: 800, color: "#E8EDF5", marginBottom: 20 }}>System Controls</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
              {[
                { label: "Maintenance Mode", desc: "Disable user access during updates", key: "maintenance", value: maintenance, setter: setMaintenance, danger: true },
                { label: "Debug Mode", desc: "Enable verbose system logging", key: "debug", value: debugMode, setter: setDebugMode, danger: false },
              ].map(c => (
                <div key={c.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px", background: c.value && c.danger ? "rgba(248,113,113,0.05)" : "rgba(255,255,255,0.02)", border: c.value && c.danger ? "1px solid rgba(248,113,113,0.2)" : "1px solid rgba(255,255,255,0.06)", borderRadius: 12, transition: "all 0.3s" }}>
                  <div>
                    <p style={{ fontSize: 14, fontWeight: 600, color: "#E8EDF5", marginBottom: 3 }}>{c.label}</p>
                    <p style={{ fontSize: 12, color: "#8892a4" }}>{c.desc}</p>
                  </div>
                  <div onClick={() => { c.setter(v => !v); showToast(`${c.label} ${!c.value ? "enabled" : "disabled"}`); }}
                    style={{ width: 44, height: 24, borderRadius: 12, background: c.value ? (c.danger ? "#F87171" : "#6EE7B7") : "rgba(255,255,255,0.1)", position: "relative", cursor: "pointer", transition: "background 0.25s", flexShrink: 0 }}>
                    <div style={{ position: "absolute", top: 3, left: c.value ? 23 : 3, width: 18, height: 18, borderRadius: "50%", background: c.value ? "#0A0E17" : "#4B5563", transition: "left 0.25s" }} />
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
              {[
                { label: "🗑️ Clear Cache", color: "#FBBF24" },
                { label: "📊 Export Logs", color: "#60A5FA" },
                { label: "🔄 Restart Services", color: "#A78BFA" },
                { label: "⚠️ Send Alert", color: "#F87171" },
              ].map((btn, i) => (
                <button key={i} onClick={() => showToast(`${btn.label.split(" ").slice(1).join(" ")} executed.`)}
                  style={{ padding: "9px 16px", background: `${btn.color}10`, border: `1px solid ${btn.color}25`, borderRadius: 9, color: btn.color, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.background = `${btn.color}20`}
                  onMouseLeave={e => e.currentTarget.style.background = `${btn.color}10`}>
                  {btn.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === "permissions" && (
        <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 16, padding: "24px" }}>
          <h3 style={{ fontSize: 15, fontWeight: 800, color: "#E8EDF5", marginBottom: 20 }}>Role Permissions</h3>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#4B5563", width: 140 }}>ROLE</th>
                  {["Dashboard","Analytics","Users","Settings","Admin Panel","Billing","API Keys"].map(p => (
                    <th key={p} style={{ padding: "12px 10px", textAlign: "center", fontSize: 11, fontWeight: 700, color: "#4B5563" }}>{p}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PERMISSIONS.map(r => (
                  <tr key={r.role} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                    <td style={{ padding: "14px 16px" }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: r.color, background: `${r.color}12`, border: `1px solid ${r.color}25`, padding: "4px 12px", borderRadius: 20 }}>{r.role}</span>
                    </td>
                    {["Dashboard","Analytics","Users","Settings","Admin Panel","Billing","API Keys"].map(p => (
                      <td key={p} style={{ padding: "14px 10px", textAlign: "center" }}>
                        {r.permissions.includes(p)
                          ? <span style={{ fontSize: 16 }}>✅</span>
                          : <span style={{ fontSize: 16, opacity: 0.3 }}>❌</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "audit log" && (
        <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 16, overflow: "hidden" }}>
          <div style={{ padding: "20px 22px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 800, color: "#E8EDF5" }}>Audit Log</h3>
              <p style={{ fontSize: 12, color: "#8892a4", marginTop: 3 }}>All system and user activity</p>
            </div>
            <button style={{ padding: "7px 14px", background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.2)", borderRadius: 8, color: "#60A5FA", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Export CSV</button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                {["User","Action","Type","IP Address","Time"].map(h => (
                  <th key={h} style={{ padding: "11px 20px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#4B5563", letterSpacing: "0.07em" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {AUDIT_LOG.map((log, i) => (
                <tr key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.04)", transition: "background 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.02)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "13px 20px", fontSize: 13, fontWeight: 600, color: "#E8EDF5" }}>{log.user}</td>
                  <td style={{ padding: "13px 20px", fontSize: 13, color: "#8892a4" }}>{log.action}</td>
                  <td style={{ padding: "13px 20px" }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: TYPE_STYLE[log.type]?.color, background: TYPE_STYLE[log.type]?.bg, padding: "3px 9px", borderRadius: 20 }}>{TYPE_STYLE[log.type]?.label}</span>
                  </td>
                  <td style={{ padding: "13px 20px" }}><code style={{ fontSize: 12, color: "#4B5563", background: "rgba(255,255,255,0.04)", padding: "2px 8px", borderRadius: 5 }}>{log.ip}</code></td>
                  <td style={{ padding: "13px 20px", fontSize: 12, color: "#4B5563" }}>{log.time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === "system" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {[
            { title: "Server Info", items: [["OS", "Ubuntu 24.04 LTS"], ["Node.js", "v20.11.0"], ["Database", "PostgreSQL 16"], ["Redis", "7.2.4"], ["Memory Used", "3.2 GB / 16 GB"], ["CPU Load", "12%"]] },
            { title: "App Config", items: [["Version", "v2.4.1"], ["Environment", "Production"], ["Region", "US-East-1"], ["CDN", "Enabled"], ["Rate Limit", "1000 req/min"], ["Cache TTL", "3600s"]] },
          ].map((panel, i) => (
            <div key={i} style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 16, padding: "22px" }}>
              <h3 style={{ fontSize: 15, fontWeight: 800, color: "#E8EDF5", marginBottom: 18 }}>{panel.title}</h3>
              {panel.items.map(([k, v], j) => (
                <div key={j} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                  <span style={{ fontSize: 13, color: "#8892a4" }}>{k}</span>
                  <code style={{ fontSize: 13, color: "#E8EDF5", fontWeight: 600 }}>{v}</code>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}